import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  computed,
  inject,
  signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LocalizationService } from '../../core/localization/localization.service';
import { CatalogOffering, CatalogService, CategoryResponse } from '../catalog/catalog.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { GeographicRouteSelectorComponent } from '../../shared/design-system/geographic-route-selector.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  GrowthJourneyService,
  MarketCompetitorProfile,
  UpsertMarketCompetitorProfileRequest
} from './growth-journey.service';
import { normalizeDiscoveryStatus } from './growth-status.util';

type OfferingWithCategory = CatalogOffering & { categoryId?: string | null };

@Component({
  standalone: true,
  selector: 'ain-growth-market-competitor-panel',
  providers: [MessageService],
  imports: [
    FormsModule,
    AinFormFieldComponent,
    InfoHintComponent,
    AinStateComponent,
    GeographicRouteSelectorComponent,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    TextareaModule,
    TagModule
  ],
  template: `
    <section id="market-competitors" class="market-panel">
      <div class="section-title">
        <h3>{{ t('growth.journey.market.panel.title') }}</h3>
      </div>

      <ain-info-hint
        [titleKey]="'growth.journey.market.help.title'"
        [descriptionKey]="'growth.journey.market.help.description'"
        [bulletKeys]="[
          'growth.journey.market.help.bullets.swot',
          'growth.journey.market.help.bullets.scope'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (loadError()) {
        <ain-state type="error" [title]="t('common.error')" [message]="loadError() ?? ''" />
      } @else {
        @if (model.status) {
          <div class="meta-row">
            <p-tag [value]="model.status" severity="info" />
          </div>
        }

        <div class="form-section">
          <h4>{{ t('growth.journey.market.sections.identity') }}</h4>
          <div class="grid">
            <ain-form-field [label]="t('growth.journey.market.fields.marketingName')">
              <input
                pInputText
                class="full-width"
                [(ngModel)]="model.marketingName"
                [disabled]="!isEditable()" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.journey.market.fields.marketingType')">
              <p-select
                [options]="marketingTypeOptions"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="model.marketingType"
                [disabled]="!isEditable()"
                [showClear]="true"
                appendTo="body" />
            </ain-form-field>
          </div>
        </div>

        @if (showGeoScope()) {
          <div class="form-section">
            <h4>{{ t('growth.journey.market.sections.scope') }}</h4>
            <div class="grid">
              <ain-form-field [label]="t('growth.journey.market.fields.geoArea')">
                <ain-geographic-route-selector [(ngModel)]="model.geoAreaId" />
              </ain-form-field>
              <ain-form-field [label]="t('growth.journey.market.fields.category')">
                <p-select
                  [options]="categoryOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [(ngModel)]="model.categoryId"
                  [disabled]="!isEditable()"
                  [showClear]="true"
                  appendTo="body"
                  (ngModelChange)="onCategoryChanged($event)" />
              </ain-form-field>
              <ain-form-field [label]="t('growth.journey.market.fields.offering')">
                <p-select
                  [options]="offeringOptions"
                  optionLabel="label"
                  optionValue="value"
                  [(ngModel)]="model.offeringId"
                  [disabled]="!isEditable()"
                  [showClear]="true"
                  appendTo="body"
                  (ngModelChange)="onOfferingChanged($event)" />
              </ain-form-field>
            </div>
          </div>
        } @else {
          <div class="form-section">
            <h4>{{ t('growth.journey.market.sections.scope') }}</h4>
            <div class="grid">
              <ain-form-field [label]="t('growth.journey.market.fields.category')">
                <p-select
                  [options]="categoryOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [(ngModel)]="model.categoryId"
                  [disabled]="!isEditable()"
                  [showClear]="true"
                  appendTo="body"
                  (ngModelChange)="onCategoryChanged($event)" />
              </ain-form-field>
              <ain-form-field [label]="t('growth.journey.market.fields.offering')">
                <p-select
                  [options]="offeringOptions"
                  optionLabel="label"
                  optionValue="value"
                  [(ngModel)]="model.offeringId"
                  [disabled]="!isEditable()"
                  [showClear]="true"
                  appendTo="body"
                  (ngModelChange)="onOfferingChanged($event)" />
              </ain-form-field>
            </div>
          </div>
        }

        <div class="form-section">
          <h4>{{ t('growth.journey.market.sections.size') }}</h4>
          <div class="grid">
            <ain-form-field [label]="t('growth.journey.market.fields.marketVolume')">
              <p-inputNumber
                [(ngModel)]="model.marketVolume"
                [disabled]="!isEditable()"
                [useGrouping]="false"
                [minFractionDigits]="0"
                [maxFractionDigits]="0" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.journey.market.fields.marketValue')">
              <p-inputNumber
                [(ngModel)]="model.marketValue"
                [disabled]="!isEditable()"
                [minFractionDigits]="0"
                [maxFractionDigits]="4" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.journey.market.fields.shareByVolume')">
              <p-inputNumber
                [(ngModel)]="model.marketShareByVolume"
                [disabled]="!isEditable()"
                [minFractionDigits]="0"
                [maxFractionDigits]="4" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.journey.market.fields.shareByValue')">
              <p-inputNumber
                [(ngModel)]="model.marketShareByValue"
                [disabled]="!isEditable()"
                [minFractionDigits]="0"
                [maxFractionDigits]="4" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.journey.market.fields.volumeTargeting')">
              <p-inputNumber
                [(ngModel)]="model.volumeTargeting"
                [disabled]="!isEditable()"
                [minFractionDigits]="0"
                [maxFractionDigits]="4" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.journey.market.fields.valueTargeting')">
              <p-inputNumber
                [(ngModel)]="model.valueTargeting"
                [disabled]="!isEditable()"
                [minFractionDigits]="0"
                [maxFractionDigits]="4" />
            </ain-form-field>
          </div>
        </div>

        <div class="form-section">
          <h4>{{ t('growth.journey.market.sections.competitors') }}</h4>
          <div class="grid">
            <ain-form-field [label]="t('growth.journey.market.fields.numberOfCompetitors')">
              <p-inputNumber
                [(ngModel)]="model.numberOfCompetitors"
                [disabled]="!isEditable()"
                [useGrouping]="false"
                [minFractionDigits]="0"
                [maxFractionDigits]="0" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.journey.market.fields.ourRank')">
              <p-inputNumber
                [(ngModel)]="model.ourPositionAmongCompetitors"
                [disabled]="!isEditable()"
                [useGrouping]="false"
                [minFractionDigits]="0"
                [maxFractionDigits]="0" />
            </ain-form-field>
          </div>

          <div class="grid competitor-rows">
            <ain-form-field [label]="t('growth.journey.market.fields.competitorOneName')">
              <input pInputText class="full-width" [(ngModel)]="model.competitorOneName" [disabled]="!isEditable()" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.journey.market.fields.competitorOneShare')">
              <p-inputNumber
                [(ngModel)]="model.competitorOneShare"
                [disabled]="!isEditable()"
                [minFractionDigits]="0"
                [maxFractionDigits]="4" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.journey.market.fields.competitorTwoName')">
              <input pInputText class="full-width" [(ngModel)]="model.competitorTwoName" [disabled]="!isEditable()" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.journey.market.fields.competitorTwoShare')">
              <p-inputNumber
                [(ngModel)]="model.competitorTwoShare"
                [disabled]="!isEditable()"
                [minFractionDigits]="0"
                [maxFractionDigits]="4" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.journey.market.fields.competitorThreeName')">
              <input pInputText class="full-width" [(ngModel)]="model.competitorThreeName" [disabled]="!isEditable()" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.journey.market.fields.competitorThreeShare')">
              <p-inputNumber
                [(ngModel)]="model.competitorThreeShare"
                [disabled]="!isEditable()"
                [minFractionDigits]="0"
                [maxFractionDigits]="4" />
            </ain-form-field>
          </div>
        </div>

        <div class="form-section">
          <h4>{{ t('growth.journey.market.sections.leader') }}</h4>
          <div class="grid">
            <ain-form-field [label]="t('growth.journey.market.fields.leaderPrice')">
              <p-select
                [options]="leaderPositionOptions"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="model.marketLeaderPricePosition"
                [disabled]="!isEditable()"
                [showClear]="true"
                appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.journey.market.fields.leaderQuality')">
              <p-select
                [options]="leaderQualityOptions"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="model.marketLeaderQualityPosition"
                [disabled]="!isEditable()"
                [showClear]="true"
                appendTo="body" />
            </ain-form-field>
          </div>
          <ain-form-field [label]="t('growth.journey.market.fields.leaderStrengths')">
            <textarea pInputTextarea rows="3" class="full-width" [(ngModel)]="model.marketLeaderStrengths" [disabled]="!isEditable()"></textarea>
          </ain-form-field>
          <ain-form-field [label]="t('growth.journey.market.fields.leaderWeaknesses')">
            <textarea pInputTextarea rows="3" class="full-width" [(ngModel)]="model.marketLeaderWeaknesses" [disabled]="!isEditable()"></textarea>
          </ain-form-field>
          <ain-form-field [label]="t('growth.journey.market.fields.otherStrategies')">
            <textarea pInputTextarea rows="3" class="full-width" [(ngModel)]="model.otherCompetitorsStrategies" [disabled]="!isEditable()"></textarea>
          </ain-form-field>
        </div>

        <div class="form-section">
          <h4>{{ t('growth.journey.market.sections.positioning') }}</h4>
          <div class="grid">
            <ain-form-field [label]="t('growth.journey.market.fields.ourPrice')">
              <p-select
                [options]="ourPositionOptions"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="model.ourPricePosition"
                [disabled]="!isEditable()"
                [showClear]="true"
                appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.journey.market.fields.ourQuality')">
              <p-select
                [options]="ourQualityOptions"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="model.ourQualityPosition"
                [disabled]="!isEditable()"
                [showClear]="true"
                appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.journey.market.fields.entryStrategy')">
              <p-select
                [options]="entryStrategyOptions"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="model.entryStrategy"
                [disabled]="!isEditable()"
                [showClear]="true"
                appendTo="body" />
            </ain-form-field>
          </div>
        </div>

        <div class="form-section">
          <h4>{{ t('growth.journey.market.sections.differentiation') }}</h4>
          <ain-form-field [label]="t('growth.journey.market.fields.differenceFromCompetitors')">
            <textarea pInputTextarea rows="3" class="full-width" [(ngModel)]="model.differenceFromCompetitors" [disabled]="!isEditable()"></textarea>
          </ain-form-field>
          <ain-form-field [label]="t('growth.journey.market.fields.whyChooseUs')">
            <textarea pInputTextarea rows="3" class="full-width" [(ngModel)]="model.whyCustomersChooseUs" [disabled]="!isEditable()"></textarea>
          </ain-form-field>
        </div>

        @if (isEditable()) {
          <div class="actions">
            <button
              pButton
              type="button"
              [label]="t('common.save')"
              [loading]="saving()"
              (click)="save()"></button>
          </div>
        }
      }

      @if (actionError()) {
        <p class="action-error">{{ actionError() }}</p>
      }
    </section>
  `,
  styles: [`
    .market-panel { border-top: 1px solid #e5e7eb; padding-top: 12px; display: grid; gap: 12px; }
    .section-title h3, .form-section h4 { margin: 0 0 8px; }
    .form-section { display: grid; gap: 10px; border-top: 1px solid #f3f4f6; padding-top: 10px; }
    .form-section:first-of-type { border-top: none; padding-top: 0; }
    .grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
    .competitor-rows { margin-top: 4px; }
    .meta-row, .actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .full-width { width: 100%; }
    .action-error { margin: 0; color: #b91c1c; }
    :host ::ng-deep .p-inputnumber, :host ::ng-deep .p-select { width: 100%; }
  `]
})
export class GrowthMarketCompetitorPanelComponent implements OnInit, OnChanges {
  @Input({ required: true }) discoveryId!: string;
  @Input() discoveryStatus = '';
  @Input() readOnly = false;
  @Output() discoveryChanged = new EventEmitter<void>();

  private readonly journey = inject(GrowthJourneyService);
  private readonly catalog = inject(CatalogService);
  private readonly i18n = inject(LocalizationService);
  private readonly messages = inject(MessageService);

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly actionError = signal<string | null>(null);
  readonly categories = signal<CategoryResponse[]>([]);
  readonly offerings = signal<CatalogOffering[]>([]);

  model: UpsertMarketCompetitorProfileRequest & { status?: string | null } = this.createEmptyModel();

  readonly categoryOptions = computed(() =>
    this.categories().map(item => ({ value: item.id, label: item.name }))
  );

  get offeringOptions(): Array<{ value: string; label: string }> {
    const categoryId = this.model.categoryId;
    const all = this.offerings();
    const filtered = categoryId
      ? all.filter(offering => this.matchesCategory(offering, categoryId))
      : all;

    return filtered.map(item => ({ value: item.id, label: item.title }));
  }

  get marketingTypeOptions(): Array<{ value: string; label: string }> {
    return [
      { value: 'InternalNationwide', label: this.t('growth.journey.market.marketingType.internalNationwide') },
      { value: 'InternalByProvince', label: this.t('growth.journey.market.marketingType.internalByProvince') },
      { value: 'InternalByCity', label: this.t('growth.journey.market.marketingType.internalByCity') },
      { value: 'InternalByRegion', label: this.t('growth.journey.market.marketingType.internalByRegion') }
    ];
  }

  get leaderPositionOptions(): Array<{ value: string; label: string }> {
    return [
      { value: 'HigherThanUs', label: this.t('growth.journey.market.leaderPrice.higherThanUs') },
      { value: 'LowerThanUs', label: this.t('growth.journey.market.leaderPrice.lowerThanUs') },
      { value: 'SameAsUs', label: this.t('growth.journey.market.leaderPrice.sameAsUs') }
    ];
  }

  get leaderQualityOptions(): Array<{ value: string; label: string }> {
    return [
      { value: 'HigherThanUs', label: this.t('growth.journey.market.leaderQuality.higherThanUs') },
      { value: 'LowerThanUs', label: this.t('growth.journey.market.leaderQuality.lowerThanUs') },
      { value: 'SameAsUs', label: this.t('growth.journey.market.leaderQuality.sameAsUs') }
    ];
  }

  get ourPositionOptions(): Array<{ value: string; label: string }> {
    return [
      { value: 'HigherThanMarket', label: this.t('growth.journey.market.ourPrice.higherThanMarket') },
      { value: 'LowerThanMarket', label: this.t('growth.journey.market.ourPrice.lowerThanMarket') },
      { value: 'SameAsMarket', label: this.t('growth.journey.market.ourPrice.sameAsMarket') }
    ];
  }

  get ourQualityOptions(): Array<{ value: string; label: string }> {
    return [
      { value: 'HigherThanMarket', label: this.t('growth.journey.market.ourQuality.higherThanMarket') },
      { value: 'LowerThanMarket', label: this.t('growth.journey.market.ourQuality.lowerThanMarket') },
      { value: 'SameAsMarket', label: this.t('growth.journey.market.ourQuality.sameAsMarket') }
    ];
  }

  get entryStrategyOptions(): Array<{ value: string; label: string }> {
    return [
      { value: 'Aggressive', label: this.t('growth.journey.market.entryStrategy.aggressive') },
      { value: 'Defensive', label: this.t('growth.journey.market.entryStrategy.defensive') },
      { value: 'Conservative', label: this.t('growth.journey.market.entryStrategy.conservative') },
      { value: 'Competitive', label: this.t('growth.journey.market.entryStrategy.competitive') }
    ];
  }

  ngOnInit(): void {
    this.loadAll();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['discoveryId'] && !changes['discoveryId'].firstChange) {
      this.loadAll();
    }
  }

  isEditable(): boolean {
    return !this.readOnly && normalizeDiscoveryStatus(this.discoveryStatus) !== 'Archived';
  }

  showGeoScope(): boolean {
    return this.model.marketingType !== 'InternalNationwide' && this.model.marketingType !== null;
  }

  onCategoryChanged(categoryId: string | null): void {
    const category = this.categories().find(item => item.id === categoryId);
    this.model.categoryName = category?.name ?? null;

    if (this.model.offeringId && categoryId) {
      const offering = this.offerings().find(item => item.id === this.model.offeringId);
      if (offering && !this.matchesCategory(offering, categoryId)) {
        this.model.offeringId = null;
        this.model.offeringName = null;
      }
    }
  }

  onOfferingChanged(offeringId: string | null): void {
    const offering = this.offerings().find(item => item.id === offeringId);
    this.model.offeringName = offering?.title ?? null;
  }

  save(): void {
    if (!this.isEditable() || this.saving()) {
      return;
    }

    this.saving.set(true);
    this.actionError.set(null);

    this.journey.saveMarketProfile(this.discoveryId, this.toSaveRequest()).subscribe({
      next: profile => {
        this.applyProfile(profile);
        this.saving.set(false);
        this.messages.add({ severity: 'success', summary: this.t('common.saved') });
        this.discoveryChanged.emit();
      },
      error: err => {
        this.saving.set(false);
        this.actionError.set(this.resolveActionError(err));
      }
    });
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }

  private loadAll(): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.actionError.set(null);

    forkJoin({
      profile: this.journey.getMarketProfile(this.discoveryId),
      categories: this.catalog.listCategories().pipe(catchError(() => of([] as CategoryResponse[]))),
      offerings: this.catalog.listOfferings().pipe(catchError(() => of([] as CatalogOffering[])))
    }).subscribe({
      next: ({ profile, categories, offerings }) => {
        this.categories.set(categories);
        this.offerings.set(offerings);
        if (profile) {
          this.applyProfile(profile);
        } else {
          this.model = this.createEmptyModel();
        }
        this.loading.set(false);
      },
      error: err => {
        this.loading.set(false);
        this.loadError.set(this.resolveActionError(err));
      }
    });
  }

  private applyProfile(profile: MarketCompetitorProfile): void {
    this.model = {
      marketingName: profile.marketingName,
      marketingType: profile.marketingType,
      geoAreaId: profile.geoAreaId,
      geoAreaPath: profile.geoAreaPath,
      categoryId: profile.categoryId,
      categoryName: profile.categoryName,
      offeringId: profile.offeringId,
      offeringName: profile.offeringName,
      marketVolume: profile.marketVolume,
      marketValue: profile.marketValue,
      marketShareByVolume: profile.marketShareByVolume,
      marketShareByValue: profile.marketShareByValue,
      volumeTargeting: profile.volumeTargeting,
      valueTargeting: profile.valueTargeting,
      numberOfCompetitors: profile.numberOfCompetitors,
      ourPositionAmongCompetitors: profile.ourPositionAmongCompetitors,
      competitorOneName: profile.competitorOneName,
      competitorOneShare: profile.competitorOneShare,
      competitorTwoName: profile.competitorTwoName,
      competitorTwoShare: profile.competitorTwoShare,
      competitorThreeName: profile.competitorThreeName,
      competitorThreeShare: profile.competitorThreeShare,
      marketLeaderPricePosition: profile.marketLeaderPricePosition,
      marketLeaderQualityPosition: profile.marketLeaderQualityPosition,
      marketLeaderStrengths: profile.marketLeaderStrengths,
      marketLeaderWeaknesses: profile.marketLeaderWeaknesses,
      otherCompetitorsStrategies: profile.otherCompetitorsStrategies,
      ourPricePosition: profile.ourPricePosition,
      ourQualityPosition: profile.ourQualityPosition,
      entryStrategy: profile.entryStrategy,
      differenceFromCompetitors: profile.differenceFromCompetitors,
      whyCustomersChooseUs: profile.whyCustomersChooseUs,
      status: profile.status
    };
  }

  private toSaveRequest(): UpsertMarketCompetitorProfileRequest {
    return {
      marketingName: this.model.marketingName?.trim() ?? '',
      marketingType: this.model.marketingType,
      geoAreaId: this.model.geoAreaId,
      geoAreaPath: this.model.geoAreaPath,
      categoryId: this.model.categoryId,
      categoryName: this.model.categoryName,
      offeringId: this.model.offeringId,
      offeringName: this.model.offeringName,
      marketVolume: this.model.marketVolume,
      marketValue: this.model.marketValue,
      marketShareByVolume: this.model.marketShareByVolume,
      marketShareByValue: this.model.marketShareByValue,
      volumeTargeting: this.model.volumeTargeting,
      valueTargeting: this.model.valueTargeting,
      numberOfCompetitors: this.model.numberOfCompetitors,
      ourPositionAmongCompetitors: this.model.ourPositionAmongCompetitors,
      competitorOneName: this.model.competitorOneName,
      competitorOneShare: this.model.competitorOneShare,
      competitorTwoName: this.model.competitorTwoName,
      competitorTwoShare: this.model.competitorTwoShare,
      competitorThreeName: this.model.competitorThreeName,
      competitorThreeShare: this.model.competitorThreeShare,
      marketLeaderPricePosition: this.model.marketLeaderPricePosition,
      marketLeaderQualityPosition: this.model.marketLeaderQualityPosition,
      marketLeaderStrengths: this.model.marketLeaderStrengths,
      marketLeaderWeaknesses: this.model.marketLeaderWeaknesses,
      otherCompetitorsStrategies: this.model.otherCompetitorsStrategies,
      ourPricePosition: this.model.ourPricePosition,
      ourQualityPosition: this.model.ourQualityPosition,
      entryStrategy: this.model.entryStrategy,
      differenceFromCompetitors: this.model.differenceFromCompetitors,
      whyCustomersChooseUs: this.model.whyCustomersChooseUs,
      status: this.model.status ?? 'Draft'
    };
  }

  private createEmptyModel(): UpsertMarketCompetitorProfileRequest & { status?: string | null } {
    return {
      marketingName: '',
      marketingType: null,
      geoAreaId: null,
      geoAreaPath: null,
      categoryId: null,
      categoryName: null,
      offeringId: null,
      offeringName: null,
      marketVolume: null,
      marketValue: null,
      marketShareByVolume: null,
      marketShareByValue: null,
      volumeTargeting: null,
      valueTargeting: null,
      numberOfCompetitors: null,
      ourPositionAmongCompetitors: null,
      competitorOneName: null,
      competitorOneShare: null,
      competitorTwoName: null,
      competitorTwoShare: null,
      competitorThreeName: null,
      competitorThreeShare: null,
      marketLeaderPricePosition: null,
      marketLeaderQualityPosition: null,
      marketLeaderStrengths: null,
      marketLeaderWeaknesses: null,
      otherCompetitorsStrategies: null,
      ourPricePosition: null,
      ourQualityPosition: null,
      entryStrategy: null,
      differenceFromCompetitors: null,
      whyCustomersChooseUs: null,
      status: 'Draft'
    };
  }

  private matchesCategory(offering: CatalogOffering, categoryId: string): boolean {
    const categoryField = (offering as OfferingWithCategory).categoryId;
    if (categoryField === undefined || categoryField === null) {
      return true;
    }

    return categoryField === categoryId;
  }

  private resolveActionError(error: unknown): string {
    const code = this.extractErrorCode(error);
    if (code === 'growth_discovery_not_found') {
      return this.t('common.notFound');
    }

    if (code === 'growth_discovery_archived_immutable') {
      return this.t('growth.journey.market.errors.archivedImmutable');
    }

    if (code === 'growth_market_profile_name_required') {
      return this.t('growth.journey.market.errors.nameRequired');
    }

    return code ?? this.t('common.error');
  }

  private extractErrorCode(error: unknown): string | null {
    if (!(error instanceof HttpErrorResponse)) {
      return null;
    }

    const body = error.error as Record<string, unknown> | null;
    if (!body) {
      return null;
    }

    if (typeof body['error'] === 'string') {
      return body['error'];
    }

    if (typeof body['Error'] === 'string') {
      return body['Error'];
    }

    return null;
  }
}
